import React from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutGrid, Contact, ReceiptText, HardHat,
  BellRing, CalendarDays, Library, Settings, LogOut,
  PanelLeftClose, PanelLeftOpen, X, Route, UsersRound,
  ShoppingBag, Banknote, ArrowUpRight, ChevronDown, Boxes, Clock
} from 'lucide-react';
import { ViewState } from '../types';
import { useNavigation, viewToPathMap, ALWAYS_ALLOWED_VIEWS } from '../contexts/NavigationContext';
import { canUseAI, canManageTeam, isClockOnlyRole } from '../constants/roles';
import { useTimeClockCtx } from '../contexts/TimeClockContext';
import { useAuth } from '../contexts/AuthContext';
import { useEstimates } from '../contexts/EstimatesContext';
import { EstimateStatus } from '../types';
import { Avatar } from './ui/Avatar';
import { IconoBotRaza } from './ui/IconoBotRaza';
import { useIdioma } from '../i18n/idioma';

interface SidebarProps {
  currentView: ViewState;
  onViewChange: (view: ViewState) => void;
  isOpen: boolean;
  onClose: () => void;
  /** Rail de solo íconos en desktop (68px). En móvil el drawer siempre abre completo. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

/** Línea divisoria sutil entre zonas del sidebar */
const Hairline: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`border-t border-white/[0.06] ${className}`} aria-hidden="true" />
);

const Sidebar: React.FC<SidebarProps> = ({ currentView, onViewChange, isOpen, onClose, collapsed = false, onToggleCollapse }) => {
  const { navigateTo } = useNavigation();
  const { t, idioma, cambiarIdioma } = useIdioma();
  const { teamMembers, activeTimeLogs } = useTimeClockCtx();
  const { currentUser, logout, userRole, allowedViews } = useAuth();
  const { estimates } = useEstimates();
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const profileRef = React.useRef<HTMLDivElement>(null);

  // Cerrar el menú del perfil al hacer clic fuera o con Escape.
  React.useEffect(() => {
    if (!showProfileMenu) return;
    const onPointer = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setShowProfileMenu(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowProfileMenu(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [showProfileMenu]);

  const currentMember = teamMembers.find(m => m.email === currentUser?.email);
  const displayPhoto = currentMember?.photoUrl || currentUser?.photoURL;

  // El atajo se anuncia con la tecla de la plataforma, no con una genérica.
  const shortcutLabel = React.useMemo(() => {
    const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
    return isMac ? '⌘B' : 'Ctrl+B';
  }, []);

  const handleNavClick = () => {
    onClose();
  };

  /* Contadores de la navegación.
   *
   * Regla: solo lleva número lo que es TRABAJO PENDIENTE del usuario y que
   * caduca al atenderlo. Un conteo de "cuántos clientes tengo" no es una tarea
   * y solo ensucia; "cuántos estimados esperan aprobación" sí lo es.
   * Por eso Clientes, Precios, Proyectos, Rutas, Finanzas y Agenda NO llevan.
   * No añaden filas: viven dentro de las que ya existían. */
  // El contador de Follow-ups salia de las llamadas vencidas. Al retirarse la
  // seccion de Llamadas se queda sin fuente, y la fila va sin numero antes que
  // mostrar uno inventado.
  const awaitingApproval = React.useMemo(
    () => estimates.filter(e => e.status === EstimateStatus.SENT).length,
    [estimates],
  );

  // Gente checada AHORA: es estado en vivo, no una tarea pendiente, así que se
  // dibuja como punto y no como número (ver `live` en la fila).
  const onTheClock = activeTimeLogs.length;

  /* Íconos del OFICIO, no del catálogo genérico de SaaS.
   * Antes: Briefcase para Proyectos, FileText para Estimados, Users para
   * Clientes, Package para Precios — el set que trae cualquier plantilla.
   * Ahora cada glifo dice de qué habla en un negocio de construcción:
   * casco para la obra, ruta para las visitas, recibo para el estimado.
   * Se mantiene lucide (una sola familia de íconos en toda la app) y stroke 2. */
  /* El peón ve UNA pantalla: su reloj. No es solo esconder filas — el guardia de
   * NavigationContext lo devuelve al reloj si escribe cualquier otra ruta. */
  const soloReloj = isClockOnlyRole(userRole);

  const menuGroups: { label: string; items: { id: ViewState; label: string; icon: React.ReactNode; count?: number }[] }[] = soloReloj ? [
    {
      label: t('nav.grupo.equipo'),
      items: [
        { id: 'TimeClock' as ViewState, label: t('nav.reloj'), icon: <Clock size={16} strokeWidth={2} /> },
      ],
    },
  ] : [
    {
      label: t('nav.grupo.ventas'),
      items: [
        { id: 'Dashboard', label: t('nav.panel'), icon: <LayoutGrid size={16} strokeWidth={2} /> },
        { id: 'Clients', label: t('nav.clientes'), icon: <Contact size={16} strokeWidth={2} /> },
        { id: 'Estimates', label: t('nav.estimados'), icon: <ReceiptText size={16} strokeWidth={2} />, count: awaitingApproval },
        { id: 'Pricebook', label: t('nav.precios'), icon: <Library size={16} strokeWidth={2} /> },
        { id: 'FollowUps', label: t('nav.seguimientos'), icon: <BellRing size={16} strokeWidth={2} /> },
      ],
    },
    {
      /* "Finance" era un grupo con UN solo ítem: una etiqueta entera para un
       * elemento. Se fusiona en Operations — menos rótulos, más aire real. */
      label: t('nav.grupo.operaciones'),
      items: [
        { id: 'Projects', label: t('nav.proyectos'), icon: <HardHat size={16} strokeWidth={2} /> },
        { id: 'Services', label: t('nav.rutas'), icon: <Route size={16} strokeWidth={2} /> },
        { id: 'Inventory', label: t('nav.inventario'), icon: <Boxes size={16} strokeWidth={2} /> },
        { id: 'Finance', label: t('nav.finanzas'), icon: <Banknote size={16} strokeWidth={2} /> },
      ],
    },
    {
      label: t('nav.grupo.equipo'),
      items: [
        /* Dos entradas distintas para la misma zona.
         *
         * El oficinista entra por «Gente» (/workforce): directorio, horas, mapa.
         * El de campo entra por «Reloj» (/timeclock), que abre directamente su
         * pestaña de checado — lo único que su rol puede ver ahí.
         *
         * Esto ADEMÁS arregla que la cuadrilla se quedara sin barra lateral:
         * TeamMemberModal da `allowedViews: ['TimeClock']` por defecto, pero el
         * único ítem de este grupo era 'Workforce', así que ese permiso no
         * casaba con nada y el trabajador no veía ni su propio reloj. */
        canManageTeam(userRole)
          ? { id: 'Workforce' as ViewState, label: t('nav.gente'), icon: <UsersRound size={16} strokeWidth={2} />, count: onTheClock }
          : { id: 'TimeClock' as ViewState, label: t('nav.reloj'), icon: <Clock size={16} strokeWidth={2} /> },
        { id: 'Calendar', label: t('nav.agenda'), icon: <CalendarDays size={16} strokeWidth={2} /> },
      ],
    },
  ];

  /* Fila estándar del sidebar — un solo patrón para nav, chat y store.
   * 32px de alto: la densidad de una herramienta profesional (Linear/Raycast),
   * no la de una landing. El estado activo es un relleno translúcido: se lee
   * sobre el navy sin introducir un segundo color ni una barra lateral. */
  /** Cada idioma escrito en sí mismo: así lo reconoce quien no entiende el otro. */
  const IDIOMAS = [['es', 'Español'], ['en', 'English']] as const;

  /* Alto de fila: 28px en móvil, 32px de lg en adelante.
     El cajón del móvil tiene que caber ENTERO sin scroll — once destinos, tres
     rótulos de grupo y el pie no entran a 32px en un teléfono corto, y la lista
     abría desplazada con «Dashboard» ya fuera de vista. */
  // `touch-target-auto` opts these rows out of the global mobile rule that forces
  // every button/anchor to min-height 44px. That rule is right for isolated
  // controls, but here it inflated twelve nav rows from 28px to 44px and pushed
  // the footer off a phone screen — the menu had to be scrolled to reach half
  // the app. A full-width 36px row is a 220×36 target: four times the area of
  // the 44×44 square the rule protects, so nothing gets harder to tap.
  const rowBase = `group relative w-full flex items-center gap-2.5 h-9 lg:h-8 touch-target-auto px-2 rounded-md transition-colors duration-150 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${collapsed ? 'lg:justify-center lg:px-0' : ''}`;

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-navy-950/40 z-40 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar Container
          Sin `h-screen` ni `h-[100dvh]`: el alto lo da `inset-y-0`.

          Aquí ponía las DOS, con la intención de «usa dvh, y si no, cae a vh».
          En Tailwind eso NO funciona: quien gana no es la clase que escribes
          después, sino la que sale después EN LA HOJA. Comprobado en el CSS
          compilado —`.h-[100dvh]` en el byte 14879, `.h-screen` en el 15560—,
          así que mandaba `100vh` y el `dvh` era letra muerta.

          Y `100vh` en iOS mide SIEMPRE la ventana con las barras retraídas. El
          cajón se dibujaba más alto que lo visible y el pie —idioma, Chat,
          Tienda, y la tarjeta con Ajustes y Cerrar sesión— quedaba debajo de la
          barra de Safari. Sin rescate por scroll: esos bloques están fuera del
          área que scrollea. El comentario de arriba dice que este cajón «tiene
          que caber ENTERO sin scroll», y por esto no cabía.

          Medido en el motor de Safari con la barra desplegada (ventana de
          745px): ANTES el cajón salía de 9667px y se pasaba 8922 por abajo, con
          el pie fuera; AHORA mide 745 exactos y el pie se ve. Con la barra
          retraída (844px) las dos versiones caben — por eso en escritorio esto
          no se notaba nunca.

          `inset-y-0` clava arriba y abajo contra el viewport real, que es lo que
          se quería, y no depende de ninguna unidad. Pasa lo mismo en Chrome de
          Android; no es solo de iPhone. */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-[calc(220px+env(safe-area-inset-left))] ${collapsed ? 'lg:w-[68px]' : 'lg:w-[220px]'} bg-navy-850 text-white flex flex-col border-r border-white/[0.06] pb-segura pl-segura
        transform transition-[transform,width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]
        lg:translate-x-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>

        {/* Brand Header.
            El control de colapsar vivía flotando a media altura del borde
            derecho, sin relación con nada. Ahora es una fila del header: se
            asocia con la barra que controla y deja de partir el borde. */}
        <div className={`h-12 lg:h-14 shrink-0 flex items-center ${collapsed ? 'lg:justify-center lg:px-0 px-3' : 'px-3'}`}>
          <Link to="/dashboard" onClick={handleNavClick} className="flex items-center gap-2.5 cursor-pointer rounded-lg px-1 py-1 -mx-1 hover:bg-white/[0.04] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
            <img
              src="/logo.webp"
              alt="Raza Pro"
              className="h-7 w-auto object-contain"
            />
            <span className={`font-semibold tracking-tight text-[14px] text-white ${collapsed ? 'lg:hidden' : ''}`}>
              Raza Pro
            </span>
          </Link>

          {/* Un chevron pelado no dice qué hace: se lee como "atrás".
              PanelLeftClose dibuja literalmente un panel plegándose, así que el
              ícono explica la acción sin texto. Se acompaña del atajo ⌘B (el
              universal para alternar barra lateral: VS Code, Notion, Linear),
              anunciado en el tooltip para que se aprenda solo. */}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              aria-label={t('aria.colapsarMenu')}
              aria-keyshortcuts="Meta+B Control+B"
              title={`Colapsar menú  (${shortcutLabel})`}
              className={`hidden lg:grid ml-auto w-7 h-7 place-items-center rounded-md text-acero-300 hover:text-white hover:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${collapsed ? 'lg:hidden' : ''}`}
            >
              <PanelLeftClose size={16} strokeWidth={2} />
            </button>
          )}

          <button onClick={onClose} aria-label={t('aria.cerrarMenu')} className="lg:hidden text-acero-300 hover:text-white ml-auto transition-colors rounded-lg p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        {/* En el rail colapsado el logo ocupa el header, así que el control de
            expandir baja a su propia fila para no competir con la marca. */}
        {onToggleCollapse && collapsed && (
          <button
            onClick={onToggleCollapse}
            aria-label={t('aria.expandirMenu')}
            aria-keyshortcuts="Meta+B Control+B"
            title={`Expandir menú  (${shortcutLabel})`}
            className="hidden lg:grid mx-auto mb-1 w-8 h-8 place-items-center rounded-md text-acero-300 hover:text-white hover:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <PanelLeftOpen size={16} strokeWidth={2} />
          </button>
        )}

        {/* Navigation.
            `min-h-0` NO es decorativo. Un hijo flex arranca con
            `min-height:auto`, así que `flex-1` no lo deja encogerse por debajo
            de su contenido: con la lista larga —o con la pantalla baja del
            móvil— la nav conservaba su alto completo, `overflow-y-auto` nunca
            llegaba a hacer scroll y la zona inferior (idioma, tienda, perfil)
            se montaba encima de la última fila. Por eso el selector de idioma
            tapaba «Agenda». Con `min-h-0` la nav sí encoge y hace su scroll. */}
        <nav className="flex-1 min-h-0 px-2 overflow-y-auto pb-2 no-scrollbar">
          {menuGroups.map((group, gi) => {
            const visibleItems = group.items.filter(
              // ALWAYS_ALLOWED_VIEWS keeps this in step with the route guard, so
              // a view exempt from the guard is not simultaneously invisible in
              // the menu. Nothing in menuGroups is currently exempt — Classes
              // was and has been removed from the menu — but the two must stay
              // aligned or the next exempt view will be unreachable in practice
              // for the many non-Admins whose allowed_views is near-empty.
              //
              // `soloReloj` bypasses the allow-list entirely. Three Laborers in
              // production have an EMPTY allowed_views, and filtering their
              // two-item group by it left them a menu containing Clases and not
              // the Reloj their account exists for. The role already decides
              // what this branch may contain — filtering it again by a column
              // that is blank for most of them only subtracts.
              item => soloReloj || userRole === 'Admin' || ALWAYS_ALLOWED_VIEWS.includes(item.id) || allowedViews.includes(item.id)
            );
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.label}>
                {/* Etiqueta de grupo: sentence case y callada. Las mayúsculas
                    rastreadas eran ruido — el grupo lo define el espacio. */}
                <p className={`px-2 pb-px lg:pb-1 text-[11px] font-medium text-acero-400 ${gi === 0 ? 'pt-1' : 'pt-1.5 lg:pt-4'} ${collapsed ? 'lg:hidden' : ''}`}>
                  {group.label}
                </p>
                {/* En modo rail los grupos se separan con línea, no con texto */}
                {collapsed && gi > 0 && (
                  <Hairline className="hidden lg:block mx-2 my-2" />
                )}
                <div className="space-y-[1px]">
                  {visibleItems.map((item) => {
                    const isActive = currentView === item.id;
                    const path = viewToPathMap[item.id] || '/dashboard';

                    return (
                      <Link
                        key={item.id}
                        to={path}
                        onClick={handleNavClick}
                        aria-current={isActive ? 'page' : undefined}
                        title={collapsed ? item.label : undefined}
                        className={`${rowBase} ${isActive
                          ? 'bg-white/[0.08] text-white font-medium'
                          : 'text-acero-400 hover:bg-white/[0.04] hover:text-white font-normal'
                          }`}
                      >
                        {/* `rp-icono-activo` rellena el glifo de la sección en
                            la que estás (ver index.css). El color solo no
                            bastaba: en una lista de once íconos iguales de
                            peso, el relleno es lo que hace que uno resalte. */}
                        <span className={`transition-colors duration-150 shrink-0 ${isActive ? 'rp-icono-activo text-brand-400' : 'text-acero-300 group-hover:text-white'}`}>
                          {item.icon}
                        </span>
                        <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>

                        {/* Contador de trabajo pendiente. Número plano a la
                            derecha, en mono — nada de píldora de color: en una
                            lista de 10 ítems las píldoras se vuelven un mar de
                            manchas y deja de destacar ninguna.
                            En el rail colapsado no cabe el número, así que
                            degrada a un punto en la esquina del ícono. */}
                        {!!item.count && item.count > 0 && (
                          <>
                            {/* El contador de la fila activa va en blanco: sobre
                                el navy más claro, `acero-300` en las dos ramas
                                dejaba de distinguir activa de inactiva. */}
                            <span className={`ml-auto shrink-0 font-data text-[11px] tabular-nums ${isActive ? 'text-white' : 'text-acero-300'} ${collapsed ? 'lg:hidden' : ''}`}>
                              {item.count > 99 ? '99+' : item.count}
                            </span>
                            {collapsed && (
                              <span
                                className="hidden lg:block absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-brand-400"
                                aria-hidden="true"
                              />
                            )}
                          </>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Zona inferior — mismo patrón de fila que la navegación */}
        <div className="px-2 pb-2 space-y-[1px] shrink-0">
          {/* Selector de idioma.
              Estaba dentro del menú del perfil y nadie lo encontraba: había que
              adivinar que vivía detrás de tu propia foto. Aquí está a la vista
              sin abrir nada, y en el sitio donde ya se busca lo que no es
              navegación.

              Los dos nombres van escritos en SU PROPIO idioma ("Español",
              "English"), que es como los reconoce alguien que no entiende el
              otro — que es justamente quien más necesita este botón. Por eso
              tampoco se traducen ni llevan bandera: una bandera es un país, no
              una lengua, y el español de estos contratistas no es el de ninguna
              bandera en particular.

              Se enseñan las dos opciones, no un interruptor: con un interruptor
              hay que deducir qué pasa al tocarlo. Aquí se ve cuál está puesto y
              cuál es la otra. */}
          <div className={`pt-0.5 pb-1 lg:pt-1 lg:pb-1.5 ${collapsed ? 'lg:pt-0' : ''}`}>
            <div
              role="radiogroup"
              aria-label={t('idioma.titulo')}
              className={`flex gap-1 rounded-lg bg-white/[0.06] p-[3px] ${collapsed ? 'lg:flex-col lg:bg-transparent lg:p-0 lg:gap-[1px]' : ''}`}
            >
              {IDIOMAS.map(([codigo, nombre]) => {
                const puesto = idioma === codigo;
                return (
                  <button
                    key={codigo}
                    type="button"
                    role="radio"
                    aria-checked={puesto}
                    lang={codigo}
                    onClick={() => cambiarIdioma(codigo)}
                    title={collapsed ? nombre : undefined}
                    className={`flex-1 h-8 lg:h-7 touch-target-auto rounded-md text-[12px] font-medium transition-colors duration-150
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                      puesto
                        ? 'bg-white text-navy-950'
                        : 'text-acero-300 hover:text-white hover:bg-white/[0.06]'
                    } ${collapsed ? 'lg:h-7 lg:w-full' : ''}`}
                  >
                    {/* En el rail colapsado no cabe "Español", pero sí "ES".
                        El nombre completo sigue estando en el `title` y en el
                        `aria-label` del grupo, así que no se pierde para quien
                        usa lector de pantalla. */}
                    {collapsed ? (
                      <>
                        <span className="lg:hidden">{nombre}</span>
                        <span className="hidden lg:inline font-data uppercase">{codigo}</span>
                      </>
                    ) : nombre}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Chat history — secondary entry; primary is the floating Ask Raza button.
              Hidden from field roles: the assistant carries full financial context. */}
          {canUseAI(userRole) && (
            <Link
              to="/ai-assistant"
              onClick={handleNavClick}
              title={collapsed ? t('nav.chat') : undefined}
              className={`${rowBase} ${currentView === 'AIAssistant'
                ? 'bg-white/[0.08] text-white font-medium'
                : 'text-acero-400 hover:bg-white/[0.04] hover:text-white font-normal'
                }`}
            >
              <IconoBotRaza size={16} strokeWidth={2} className={`shrink-0 ${currentView === 'AIAssistant' ? 'rp-icono-activo text-brand-400' : 'text-acero-300 group-hover:text-white'}`} />
              <span className={collapsed ? 'lg:hidden' : ''}>{t('nav.chat')}</span>
            </Link>
          )}

          {/* Raza Store — link externo, mismo lenguaje de fila */}
          {!soloReloj && (
          <a
            href="https://www.razaperronaacademy.com/category/all-products"
            target="_blank"
            rel="noopener noreferrer"
            title={collapsed ? t('nav.tienda') : undefined}
            className={`${rowBase} text-acero-400 hover:bg-white/[0.04] hover:text-white font-normal`}
          >
            <ShoppingBag size={16} strokeWidth={2} className="text-acero-300 group-hover:text-white shrink-0 transition-colors" />
            <span className={collapsed ? 'lg:hidden' : ''}>{t('nav.tienda')}</span>
            <ArrowUpRight size={12} className={`ml-auto text-acero-400 group-hover:text-acero-200 transition-colors ${collapsed ? 'lg:hidden' : ''}`} />
          </a>
          )}
        </div>

        {/* Pie de usuario.
            "Sign out" ya no ocupa una fila propia: es la acción menos frecuente
            de toda la barra y pesaba lo mismo que un destino de navegación.
            Ahora vive en el menú del perfil, que es donde la busca todo el
            mundo. La barra pierde una fila y gana aire sin quitar nada. */}
        <div className="px-2 pb-2.5 shrink-0 relative" ref={profileRef}>
          <Hairline className="mx-1 mb-2" />

          <button
            type="button"
            onClick={() => setShowProfileMenu(o => !o)}
            aria-haspopup="menu"
            aria-expanded={showProfileMenu}
            title={collapsed ? currentUser?.displayName || 'Account' : undefined}
            className={`flex items-center gap-2.5 w-full p-1.5 rounded-md transition-colors duration-150 group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${collapsed ? 'lg:justify-center lg:p-1' : ''} ${showProfileMenu || currentView === 'Settings' ? 'bg-white/[0.08]' : 'hover:bg-white/[0.04]'
              }`}
          >
            {/* `Avatar` en vez de un <img> suelto: pone `referrerPolicy` (sin
                ella el CDN de Google devuelve 403 y la foto se cae sola) y
                degrada a la inicial si falla, en vez de dejar el ícono de
                imagen rota del navegador. */}
            <Avatar
              src={displayPhoto}
              name={currentUser?.displayName}
              className="w-7 h-7 rounded-md bg-navy-850"
              fallbackClassName="bg-brand-400 text-navy-950 text-xs"
            />

            <div className={`text-left overflow-hidden flex-1 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
              <p className="text-[13px] font-medium truncate text-white leading-tight">{currentUser?.displayName}</p>
              <p className="text-[11px] text-acero-300 leading-tight mt-px">{userRole}</p>
            </div>

            <ChevronDown
              size={14}
              className={`text-acero-400 group-hover:text-white transition-transform shrink-0 ${showProfileMenu ? 'rotate-180' : ''} ${collapsed ? 'lg:hidden' : ''}`}
            />
          </button>

          {showProfileMenu && (
            <div
              role="menu"
              className="absolute bottom-full left-2 right-2 mb-1.5 bg-white rounded-xl border border-acero-200 shadow-s3 py-1 z-50 animate-in fade-in slide-in-from-bottom-1 duration-150"
            >
              <Link
                to="/settings"
                role="menuitem"
                onClick={() => { setShowProfileMenu(false); handleNavClick(); }}
                className="flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium text-navy-950 hover:bg-acero-50 transition-colors focus:outline-none focus-visible:bg-acero-50"
              >
                {/* `acero-600` y no `acero-300`: este menú es una tarjeta BLANCA y
                    aquel gris venía copiado de la barra oscura, donde sí se ve.
                    Medido: 300 sobre blanco da 1,63:1 — el mínimo para un icono
                    es 3:1, así que no es que se viera poco, es que no se veía.
                    600 da 5,89:1 y sigue por debajo del navy de la etiqueta. */}
                <Settings size={15} className="text-acero-600 shrink-0" /> {t('nav.ajustes')}
              </Link>

              <button
                type="button"
                role="menuitem"
                onClick={() => { setShowProfileMenu(false); logout(); }}
                className="flex items-center gap-2.5 w-full px-3 py-2 text-[13px] font-medium text-navy-950 hover:bg-acero-50 transition-colors focus:outline-none focus-visible:bg-acero-50 border-t border-acero-100 mt-1 pt-2"
              >
                {/* En rojo: además de verse (4,83:1), salir es la única acción
                    del menú que te saca de la app. El color lo dice antes de
                    leer. La etiqueta se queda navy para que el menú no parezca
                    una alarma; solo se enrojece al pasar por encima. */}
                <LogOut size={15} className="text-peligro shrink-0" /> {t('nav.salir')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
